import { useState, useEffect } from 'react'
import { User, FileText, Briefcase, GraduationCap, Award, Code, Plus, Trash2, ChevronDown, ChevronUp, ArrowUp } from 'lucide-react'

interface ResumeEditorProps {
  data: any
  onChange: (data: any) => void
  activeTab: string
  onTabChange: (tab: string) => void
}

export default function ResumeEditor({ data, onChange, activeTab, onTabChange }: ResumeEditorProps) {
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({})

  const tabs = [
    { id: 'personal', label: 'Personal', icon: User },
    { id: 'summary', label: 'Summary', icon: FileText },
    { id: 'experience', label: 'Experience', icon: Briefcase },
    { id: 'education', label: 'Education', icon: GraduationCap },
    { id: 'skills', label: 'Skills', icon: Award },
    { id: 'projects', label: 'Projects', icon: Code }
  ]

  const toggleSection = (id: string) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const updateField = (section: string, field: string, value: any) => {
    onChange({
      ...data,
      [section]: {
        ...data[section],
        [field]: value
      }
    })
  }

  const updateArrayItem = (section: string, index: number, field: string, value: any) => {
    const updated = [...data[section]]
    updated[index] = { ...updated[index], [field]: value }
    onChange({ ...data, [section]: updated })
  }

  const addArrayItem = (section: string, template: any) => {
    onChange({
      ...data,
      [section]: [...data[section], { ...template, id: Date.now().toString() }]
    })
  }

  const removeArrayItem = (section: string, index: number) => {
    onChange({
      ...data,
      [section]: data[section].filter((_: any, i: number) => i !== index)
    })
  }

  return (
    <div className="pb-6">
      {/* Tabs - Sticky */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-gray-200 pb-3 mb-4 pt-2">
        <div className="flex space-x-1 overflow-x-auto bg-gradient-to-r from-gray-50 to-gray-100 p-1 rounded-lg shadow-inner">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg font-semibold text-xs whitespace-nowrap transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-white text-blue-600 shadow-md scale-105'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-white/60'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-5">
        {/* Personal Info */}
        {activeTab === 'personal' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-200 shadow-sm">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
              <input
                type="text"
                value={data.personalInfo.fullName}
                onChange={(e) => updateField('personalInfo', 'fullName', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-base font-medium bg-white transition-all duration-200"
                placeholder="John Doe"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-xl border border-gray-200 hover:border-blue-300 transition-colors shadow-sm">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                <input
                  type="email"
                  value={data.personalInfo.email}
                  onChange={(e) => updateField('personalInfo', 'email', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-base transition-all duration-200"
                  placeholder="john@example.com"
                />
              </div>
              <div className="bg-white p-4 rounded-xl border border-gray-200 hover:border-blue-300 transition-colors shadow-sm">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={data.personalInfo.phone}
                  onChange={(e) => updateField('personalInfo', 'phone', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-base transition-all duration-200"
                  placeholder="+1 234 567 8900"
                />
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 hover:border-blue-300 transition-colors shadow-sm">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
              <input
                type="text"
                value={data.personalInfo.address}
                onChange={(e) => updateField('personalInfo', 'address', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-base transition-all duration-200"
                placeholder="City, State, Country"
              />
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border border-purple-200 shadow-sm">
              <h4 className="text-sm font-bold text-gray-800 mb-4">Social Links & Portfolio</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">LinkedIn</label>
                  <input
                    type="text"
                    value={data.personalInfo.linkedin}
                    onChange={(e) => updateField('personalInfo', 'linkedin', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 text-sm transition-all duration-200 bg-white"
                    placeholder="linkedin.com/in/..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Website</label>
                  <input
                    type="text"
                    value={data.personalInfo.website}
                    onChange={(e) => updateField('personalInfo', 'website', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 text-sm transition-all duration-200 bg-white"
                    placeholder="yoursite.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">GitHub</label>
                  <input
                    type="text"
                    value={data.personalInfo.github}
                    onChange={(e) => updateField('personalInfo', 'github', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 text-sm transition-all duration-200 bg-white"
                    placeholder="github.com/..."
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary */}
        {activeTab === 'summary' && (
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-5 rounded-xl border border-indigo-200 shadow-sm">
            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
              <FileText className="w-4 h-4 text-indigo-600 mr-2" />
              Professional Summary
            </label>
            <p className="text-xs text-gray-600 mb-3">Write a compelling summary highlighting your expertise and goals (3-5 sentences).</p>
            <textarea
              value={data.summary}
              onChange={(e) => onChange({ ...data, summary: e.target.value })}
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-base leading-relaxed transition-all duration-200 bg-white"
              placeholder="Results-driven Software Engineer with 5+ years of experience in full-stack development..."
            />
            <div className="mt-2 text-xs text-gray-500">
              {data.summary?.length || 0} characters
            </div>
          </div>
        )}

        {/* Experience */}
        {activeTab === 'experience' && (
          <div className="space-y-4">
            <div className="flex items-center mb-2">
              <Briefcase className="w-4 h-4 text-blue-600 mr-2" />
              <h3 className="text-sm font-bold text-gray-800">Work Experience</h3>
              <span className="ml-auto text-xs text-gray-500">{data.experience?.length || 0} {data.experience?.length === 1 ? 'position' : 'positions'}</span>
            </div>
            {data.experience.map((exp: any, index: number) => (
              <div
                key={exp.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md hover:border-blue-300 transition-all duration-200"
              >
                <div
                  className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 cursor-pointer"
                  onClick={() => toggleSection(`exp-${index}`)}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <Briefcase className="w-4 h-4 text-white" />
                      <div>
                        <h4 className="font-semibold text-white text-sm">
                          {exp.jobTitle || `Experience #${index + 1}`}
                        </h4>
                        <p className="text-blue-100 text-xs">{exp.company || 'Company Name'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {expandedSections[`exp-${index}`] ? (
                        <ChevronUp className="w-4 h-4 text-white" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-white" />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          removeArrayItem('experience', index)
                        }}
                        className="p-1.5 bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-white" />
                      </button>
                    </div>
                  </div>
                </div>

                {(expandedSections[`exp-${index}`] !== false) && (
                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Job Title *</label>
                        <input
                          type="text"
                          value={exp.jobTitle}
                          onChange={(e) => updateArrayItem('experience', index, 'jobTitle', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                          placeholder="Software Engineer"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Company *</label>
                        <input
                          type="text"
                          value={exp.company}
                          onChange={(e) => updateArrayItem('experience', index, 'company', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                          placeholder="Tech Corp"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Location</label>
                        <input
                          type="text"
                          value={exp.location || ''}
                          onChange={(e) => updateArrayItem('experience', index, 'location', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                          placeholder="New York, NY"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Start Date *</label>
                          <input
                            type="text"
                            value={exp.startDate}
                            onChange={(e) => updateArrayItem('experience', index, 'startDate', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                            placeholder="Jan 2022"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1.5">End Date</label>
                          <input
                            type="text"
                            value={exp.endDate}
                            onChange={(e) => updateArrayItem('experience', index, 'endDate', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                            placeholder="Present"
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Description & Achievements</label>
                      <textarea
                        value={exp.description}
                        onChange={(e) => updateArrayItem('experience', index, 'description', e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm leading-relaxed"
                        placeholder="• Led development of microservices architecture&#10;• Mentored team of 4 junior developers&#10;• Improved system performance by 40%"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
            <button
              onClick={() => addArrayItem('experience', {
                jobTitle: '',
                company: '',
                location: '',
                startDate: '',
                endDate: '',
                description: ''
              })}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-blue-600 hover:text-blue-700 font-semibold text-sm transition-all duration-200"
            >
              <Plus className="w-4 h-4" />
              <span>Add Work Experience</span>
            </button>
          </div>
        )}

        {/* Education */}
        {activeTab === 'education' && (
          <div className="space-y-4">
            <div className="flex items-center mb-2">
              <GraduationCap className="w-4 h-4 text-purple-600 mr-2" />
              <h3 className="text-sm font-bold text-gray-800">Education</h3>
              <span className="ml-auto text-xs text-gray-500">{data.education?.length || 0} {data.education?.length === 1 ? 'degree' : 'degrees'}</span>
            </div>
            {data.education.map((edu: any, index: number) => (
              <div
                key={edu.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md hover:border-purple-300 transition-all duration-200"
              >
                <div
                  className="bg-gradient-to-r from-purple-500 to-pink-600 p-3 cursor-pointer"
                  onClick={() => toggleSection(`edu-${index}`)}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <GraduationCap className="w-4 h-4 text-white" />
                      <div>
                        <h4 className="font-semibold text-white text-sm">
                          {edu.degree || `Education #${index + 1}`}
                        </h4>
                        <p className="text-purple-100 text-xs">{edu.institution || 'Institution Name'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {expandedSections[`edu-${index}`] ? (
                        <ChevronUp className="w-4 h-4 text-white" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-white" />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          removeArrayItem('education', index)
                        }}
                        className="p-1.5 bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-white" />
                      </button>
                    </div>
                  </div>
                </div>

                {(expandedSections[`edu-${index}`] !== false) && (
                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Degree *</label>
                        <input
                          type="text"
                          value={edu.degree}
                          onChange={(e) => updateArrayItem('education', index, 'degree', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 text-sm"
                          placeholder="Bachelor of Science in Computer Science"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Institution *</label>
                        <input
                          type="text"
                          value={edu.institution}
                          onChange={(e) => updateArrayItem('education', index, 'institution', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 text-sm"
                          placeholder="University Name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Location</label>
                        <input
                          type="text"
                          value={edu.location || ''}
                          onChange={(e) => updateArrayItem('education', index, 'location', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 text-sm"
                          placeholder="City, State"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Graduation Date</label>
                          <input
                            type="text"
                            value={edu.graduationDate}
                            onChange={(e) => updateArrayItem('education', index, 'graduationDate', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 text-sm"
                            placeholder="May 2020"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1.5">GPA (optional)</label>
                          <input
                            type="text"
                            value={edu.gpa}
                            onChange={(e) => updateArrayItem('education', index, 'gpa', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 text-sm"
                            placeholder="3.8"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <button
              onClick={() => addArrayItem('education', {
                degree: '',
                institution: '',
                location: '',
                graduationDate: '',
                gpa: ''
              })}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 text-purple-600 hover:text-purple-700 font-semibold text-sm transition-all duration-200"
            >
              <Plus className="w-4 h-4" />
              <span>Add Education</span>
            </button>
          </div>
        )}

        {/* Skills */}
        {activeTab === 'skills' && (
          <div className="space-y-4">
            <div className="flex items-center mb-2">
              <Award className="w-4 h-4 text-green-600 mr-2" />
              <h3 className="text-sm font-bold text-gray-800">Skills & Expertise</h3>
              <span className="ml-auto text-xs text-gray-500">{data.skills?.length || 0} {data.skills?.length === 1 ? 'category' : 'categories'}</span>
            </div>
            {data.skills.map((skillGroup: any, index: number) => (
              <div key={skillGroup.id || index} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-green-300 transition-all duration-200">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Category Name</label>
                    <input
                      type="text"
                      value={skillGroup.category}
                      onChange={(e) => updateArrayItem('skills', index, 'category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-green-400 text-sm font-medium"
                      placeholder="e.g., Programming Languages, Frameworks, Tools"
                    />
                  </div>
                  <button
                    onClick={() => removeArrayItem('skills', index)}
                    className="ml-2 p-1.5 bg-red-500 hover:bg-red-600 rounded-lg text-white transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Skills (comma separated)</label>
                  <input
                    type="text"
                    value={skillGroup.skills?.join(', ') || ''}
                    onChange={(e) => updateArrayItem('skills', index, 'skills', e.target.value.split(',').map((s: string) => s.trim()))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-green-400 text-sm"
                    placeholder="JavaScript, Python, React, Node.js"
                  />
                </div>
              </div>
            ))}
            <button
              onClick={() => addArrayItem('skills', {
                category: '',
                skills: []
              })}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 text-green-600 hover:text-green-700 font-semibold text-sm transition-all duration-200"
            >
              <Plus className="w-4 h-4" />
              <span>Add Skill Category</span>
            </button>
          </div>
        )}

        {/* Projects */}
        {activeTab === 'projects' && (
          <div className="space-y-4">
            <div className="flex items-center mb-2">
              <Code className="w-4 h-4 text-orange-600 mr-2" />
              <h3 className="text-sm font-bold text-gray-800">Projects & Portfolio</h3>
              <span className="ml-auto text-xs text-gray-500">{data.projects?.length || 0} {data.projects?.length === 1 ? 'project' : 'projects'}</span>
            </div>
            {data.projects.map((project: any, index: number) => (
              <div
                key={project.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md hover:border-orange-300 transition-all duration-200"
              >
                <div
                  className="bg-gradient-to-r from-orange-500 to-red-600 p-3 cursor-pointer"
                  onClick={() => toggleSection(`proj-${index}`)}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <Code className="w-4 h-4 text-white" />
                      <div>
                        <h4 className="font-semibold text-white text-sm">
                          {project.name || `Project #${index + 1}`}
                        </h4>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {expandedSections[`proj-${index}`] ? (
                        <ChevronUp className="w-4 h-4 text-white" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-white" />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          removeArrayItem('projects', index)
                        }}
                        className="p-1.5 bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-white" />
                      </button>
                    </div>
                  </div>
                </div>

                {(expandedSections[`proj-${index}`] !== false) && (
                  <div className="p-4 space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Project Name *</label>
                      <input
                        type="text"
                        value={project.name}
                        onChange={(e) => updateArrayItem('projects', index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 text-sm"
                        placeholder="E-Commerce Platform"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Description</label>
                      <textarea
                        value={project.description}
                        onChange={(e) => updateArrayItem('projects', index, 'description', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 text-sm leading-relaxed"
                        placeholder="Describe what the project does and your role in it..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Technologies (comma separated)</label>
                      <input
                        type="text"
                        value={project.technologies?.join(', ') || ''}
                        onChange={(e) => updateArrayItem('projects', index, 'technologies', e.target.value.split(',').map((s: string) => s.trim()))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 text-sm"
                        placeholder="React, Node.js, PostgreSQL, AWS"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
            <button
              onClick={() => addArrayItem('projects', {
                name: '',
                description: '',
                technologies: []
              })}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-orange-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 text-orange-600 hover:text-orange-700 font-semibold text-sm transition-all duration-200"
            >
              <Plus className="w-4 h-4" />
              <span>Add Project</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
